import { baseApi } from './baseApi';

/**
 * Skin Moles API - Comprehensive mole tracking and identification system
 *
 * Features:
 * - UserMole management (create, read, update, delete)
 * - MoleImage management with file upload support
 * - Combined creation endpoint (create_with_mole_choice)
 * - Mole selection and search functionality
 * - AI classification with consent management
 * - Body parts integration
 * - Image comparison and reference setting
 */

// Cache tags removed - no caching for medical data accuracy

const skinMolesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Body Parts endpoints
    fetchBodyParts: builder.query({
      query: (params = {}) => {
        const { orientation = 'front', language = 'EN' } = params;
        const queryParams = new URLSearchParams({ orientation, language }).toString();
        const fullUrl = `/body-parts/choices/?${queryParams}`;

        return {
          url: fullUrl,
          method: 'GET',
        };
      },
      // Force no caching for medical data accuracy
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // Add unique timestamp to each request to prevent caching
      forceRefetch: true,
    }),

    // UserMole endpoints
    fetchUserMoles: builder.query({
      query: (params = {}) => {
        // Filter out cache busting parameters - they're only for frontend, not for backend
        const { refresh, authToken, ...backendParams } = params;
        const queryParams = new URLSearchParams(backendParams).toString();
        const fullUrl = `/user-moles/?${queryParams}`;

        return {
          url: fullUrl,
          method: 'GET',
        };
      },
      // Force no caching for medical data accuracy
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // Add unique timestamp to each request to prevent caching
      forceRefetch: true,
    }),

    fetchUserMole: builder.query({
      query: ({ uuid }) => ({
        url: `/user-moles/${uuid}/`,
        method: 'GET',
      }),
      // Force no caching for medical data accuracy
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // Add unique timestamp to each request to prevent caching
      forceRefetch: true,
    }),

    createUserMole: builder.mutation({
      query: (params) => ({
        url: '/user-moles/',
        method: 'POST',
        body: params,
      }),
    }),

    updateUserMole: builder.mutation({
      query: ({ uuid, ...params }) => ({
        url: `/user-moles/${uuid}/`,
        method: 'PATCH',
        body: params,
      }),
    }),

    deleteUserMole: builder.mutation({
      query: ({ uuid }) => ({
        url: `/user-moles/${uuid}/?force_delete=true`,
        method: 'DELETE',
      }),
    }),

    // Moles for selection (optimized for UI selection)
    fetchMolesForSelection: builder.query({
      query: (params = {}) => ({
        url: `/user-moles/moles_for_selection/?${new URLSearchParams(params).toString()}`,
        method: 'GET',
      }),
      // Force no caching for medical data accuracy
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // Add unique timestamp to each request to prevent caching
      forceRefetch: true,
    }),

    // Search similar moles
    searchSimilarMoles: builder.query({
      query: (params) => ({
        url: `/user-moles/search_similar/?${new URLSearchParams(params).toString()}`,
        method: 'GET',
      }),
      // Force no caching for medical data accuracy
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // Add unique timestamp to each request to prevent caching
      forceRefetch: true,
    }),

    // Mole images for specific mole
    fetchMoleImages: builder.query({
      query: ({ uuid, ...params }) => {
        // Don't pass uuid as a query parameter since it's in the URL path
        const queryParams = new URLSearchParams(params).toString();
        return {
          url: `/user-moles/${uuid}/images/${queryParams ? `?${queryParams}` : ''}`,
          method: 'GET',
        };
      },
      // Force no caching for medical data accuracy
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // Add unique timestamp to each request to prevent caching
      forceRefetch: true,
    }),

    // Compare first vs latest image
    fetchMoleComparison: builder.query({
      query: ({ uuid }) => ({
        url: `/user-moles/${uuid}/comparison/`,
        method: 'GET',
      }),
      // Force no caching for medical data accuracy
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // Add unique timestamp to each request to prevent caching
      forceRefetch: true,
    }),

    // Set reference image for mole
    setMoleReferenceImage: builder.mutation({
      query: ({ uuid, image_uuid }) => ({
        url: `/user-moles/${uuid}/set_reference_image/`,
        method: 'POST',
        body: { image_uuid },
      }),
    }),

    // MoleImage endpoints
    createMoleImageWithChoice: builder.mutation({
      query: (params) => {
        const form = new FormData();

        // Handle file upload
        if (params.file) {
          form.append('file', params.file);
        }

        // Handle other parameters
        Object.keys(params).forEach((key) => {
          if (key !== 'file' && params[key] !== undefined) {
            if (typeof params[key] === 'boolean') {
              form.append(key, params[key].toString());
            } else if (Array.isArray(params[key])) {
              params[key].forEach((item) => {
                form.append(key, item);
              });
            } else {
              form.append(key, params[key]);
            }
          }
        });

        return {
          url: '/mole-images/create_with_mole_choice/',
          method: 'POST',
          body: form,
        };
      },
    }),

    // AI Classification consent
    consentAiClassification: builder.mutation({
      query: ({ image_uuid }) => ({
        url: '/mole-images/consent_ai_classification/',
        method: 'POST',
        body: { uuid: image_uuid }, // Backend expects 'uuid' not 'image_uuid'
      }),
    }),

    // Start AI classification
    classifyMoleImage: builder.mutation({
      query: ({ image_uuid, language_id }) => ({
        url: '/mole-images/classify/',
        method: 'POST',
        body: { 
          uuid: image_uuid, // Backend expects 'uuid' not 'image_uuid'
          ...(language_id && { language_id }), // Include language_id if provided
        },
      }),
    }),

    // Standard mole image operations
    fetchMoleImage: builder.query({
      query: ({ uuid }) => ({
        url: `/mole-images/${uuid}/`,
        method: 'GET',
      }),
      // Force no caching for medical data accuracy
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // Add unique timestamp to each request to prevent caching
      forceRefetch: true,
    }),

    updateMoleImage: builder.mutation({
      query: ({ uuid, ...params }) => ({
        url: `/mole-images/${uuid}/`,
        method: 'PATCH',
        body: params,
      }),
    }),

    deleteMoleImage: builder.mutation({
      query: ({ uuid }) => ({
        url: `/mole-images/${uuid}/`,
        method: 'DELETE',
      }),
    }),

    // Get all mole images (with filtering)
    fetchAllMoleImages: builder.query({
      query: (params = {}) => {
        // Filter out cache busting parameters - they're only for frontend, not for backend
        const { refresh, authToken, ...backendParams } = params;
        const queryParams = new URLSearchParams(backendParams).toString();
        const fullUrl = `/mole-images/?${queryParams}`;

        return {
          url: fullUrl,
          method: 'GET',
        };
      },
      // Force no caching for medical data accuracy
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      // Add unique timestamp to each request to prevent caching
      forceRefetch: true,
    }),

    // Get single mole image with classification status (for polling)
    fetchMoleImageStatus: builder.query({
      query: (imageUuid) => ({
        url: `/mole-images/${imageUuid}/`,
        method: 'GET',
      }),
      // Force no caching for real-time status updates
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }),
  }),
});

export const {
  // Body Parts
  useFetchBodyPartsQuery,
  useLazyFetchBodyPartsQuery,

  // User Moles
  useFetchUserMolesQuery,
  useLazyFetchUserMolesQuery,
  useFetchUserMoleQuery,
  useLazyFetchUserMoleQuery,
  useCreateUserMoleMutation,
  useUpdateUserMoleMutation,
  useDeleteUserMoleMutation,

  // Mole Selection & Search
  useFetchMolesForSelectionQuery,
  useLazyFetchMolesForSelectionQuery,
  useSearchSimilarMolesQuery,
  useLazySearchSimilarMolesQuery,

  // Mole Images for specific mole
  useFetchMoleImagesQuery,
  useLazyFetchMoleImagesQuery,
  useFetchMoleComparisonQuery,
  useLazyFetchMoleComparisonQuery,
  useSetMoleReferenceImageMutation,

  // Mole Image operations
  useCreateMoleImageWithChoiceMutation,
  useConsentAiClassificationMutation,
  useClassifyMoleImageMutation,
  useFetchMoleImageQuery,
  useLazyFetchMoleImageQuery,
  useUpdateMoleImageMutation,
  useDeleteMoleImageMutation,
  useFetchAllMoleImagesQuery,
  useLazyFetchAllMoleImagesQuery,
  useFetchMoleImageStatusQuery,
  useLazyFetchMoleImageStatusQuery,
} = skinMolesApi;
